// Schedule identity for the duplicate check in the edit drawer.
//
// Two backgrounds are DUPLICATES when they have the same schedule (same type
// and the same dates / times) AND the same priority — the resolver could not
// tell which one should show. Anything else may be saved, overlapping windows
// included: there the higher priority wins, as the drawer's hint says.
//
// The server applies the same rule (scheduleKey in
// backend/controllers/backgroundController.js) — keep the two in step.

// A comparable key for a schedule, or null when it isn't scheduled
// ("Library only") or its window isn't filled in yet.
export function scheduleKey(schedule) {
  const s = schedule || {};
  const v = (x) => String(x == null ? '' : x).trim();
  switch (s.kind) {
    case 'default':
      return 'default';
    case 'once':
    case 'yearly':
      return v(s.start) && v(s.end) ? `${s.kind}|${v(s.start)}|${v(s.end)}` : null;
    case 'timeOfDay':
      return v(s.timeStart) && v(s.timeEnd)
        ? `timeOfDay|${v(s.timeStart)}|${v(s.timeEnd)}`
        : null;
    default:
      return null; // 'library' — manual pick only, never competes
  }
}

// The first other background with the same schedule and priority, or null.
export function findScheduleDuplicate(draft, backgrounds = []) {
  const key = scheduleKey(draft?.schedule);
  if (!key) return null;
  const prio = Number(draft.priority) || 0;
  return (
    backgrounds.find(
      (b) =>
        b.id !== draft.id &&
        (Number(b.priority) || 0) === prio &&
        scheduleKey(b.schedule) === key
    ) || null
  );
}

// ── Overnight time-of-day windows ───────────────────────────────────────────
// An end time before the start is a window that runs past midnight. That is
// allowed (22:00 → 06:00 is a real night schedule), but 21:00 → 19:00 is a
// 22-hour window that is almost always a typo, so the drawer spells out the
// span and asks before saving one.
//
// Returns { minutes, label } for a valid overnight window, else null.
// label: "9:00 PM → 7:00 PM next day (22 h)".
const toMinutes = (hhmm) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const fmt12 = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const fmtSpan = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
};

export function overnightSpan(schedule) {
  const s = schedule || {};
  if (s.kind !== 'timeOfDay') return null;
  const start = toMinutes(s.timeStart);
  const end = toMinutes(s.timeEnd);
  if (start == null || end == null || end >= start) return null;
  const minutes = 1440 - start + end;
  return {
    minutes,
    label: `${fmt12(start)} → ${fmt12(end)} next day (${fmtSpan(minutes)})`,
  };
}

// ── Schedule validation ─────────────────────────────────────────────────────
// Errors for a schedule's window, keyed by field: { start?, end?, timeStart?,
// timeEnd? }. Empty object = valid. Mirrored server-side (scheduleError in
// backend/controllers/backgroundController.js) — keep the two in step.
//
//   once       YYYY-MM-DD both required; end on/after start (one day is fine)
//   yearly     MM-DD both required, real calendar days; end before start is
//              allowed — the window runs across New Year (12-20 → 01-05)
//   timeOfDay  HH:MM both required, not equal; end before start is allowed —
//              the window runs past midnight (22:00 → 06:00)
//
// `today` (YYYY-MM-DD) adds one more rule for one-time ranges: an end date
// already in the past is refused (the background would never show). Pass it
// only when the dates were changed, so an old expired background can still be
// edited.
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const MMDD = /^(\d{2})-(\d{2})$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
// Days per month in a leap year, so 02-29 is accepted for a yearly window.
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isRealDay = (s) => {
  if (!ISO_DAY.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};
const isMonthDay = (s) => {
  const m = MMDD.exec(s);
  if (!m) return false;
  const month = Number(m[1]);
  const day = Number(m[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1];
};

// `today` refuses an end date already past; `startFrom` (also YYYY-MM-DD)
// refuses a start date before it. Both are opt-in so a window that is already
// running can be saved without its existing start counting against it.
export function validateSchedule(schedule, { today, startFrom } = {}) {
  const s = schedule || {};
  const v = (x) => String(x == null ? '' : x).trim();
  const errors = {};
  if (s.kind === 'once') {
    const start = v(s.start);
    const end = v(s.end);
    if (!start) errors.start = 'Pick a start date.';
    else if (!isRealDay(start)) errors.start = 'Enter a valid start date.';
    else if (startFrom && start < startFrom) errors.start = "The start date can't be in the past.";
    if (!end) errors.end = 'Pick an end date.';
    else if (!isRealDay(end)) errors.end = 'Enter a valid end date.';
    if (!errors.start && !errors.end && end < start) {
      errors.end = "The end date can't be before the start date.";
    } else if (!errors.end && today && end < today) {
      errors.end = 'The end date has already passed — this background would never show.';
    }
  } else if (s.kind === 'yearly') {
    const start = v(s.start);
    const end = v(s.end);
    if (!start) errors.start = 'Enter a start date (MM-DD).';
    else if (!isMonthDay(start)) errors.start = 'Use MM-DD with a real date, e.g. 12-20.';
    if (!end) errors.end = 'Enter an end date (MM-DD).';
    else if (!isMonthDay(end)) errors.end = 'Use MM-DD with a real date, e.g. 12-26.';
  } else if (s.kind === 'timeOfDay') {
    const start = v(s.timeStart);
    const end = v(s.timeEnd);
    if (!start) errors.timeStart = 'Pick a start time.';
    else if (!HHMM.test(start)) errors.timeStart = 'Enter a valid start time.';
    if (!end) errors.timeEnd = 'Pick an end time.';
    else if (!HHMM.test(end)) errors.timeEnd = 'Enter a valid end time.';
    if (!errors.timeStart && !errors.timeEnd && start === end) {
      errors.timeEnd = "The end time can't be the same as the start time.";
    }
  }
  return errors;
}
